<template>
  <div class="game-grid-content">
    <!-- Content Header -->
    <div class="content-header">
      <!-- Back Button Row -->
      <div v-if="showBackButton" class="back-button-row">
        <button class="back-btn" @click="$emit('back-click')">
          {{ backButtonText }}
        </button>
      </div>

      <!-- Breadcrumb + Search Row -->
      <div class="header-row">
        <BreadcrumbNav
          :items="computedBreadcrumbItems"
          @breadcrumb-click="handleBreadcrumbClick"
        />
        <div class="search-section">
          <div
            class="search-container"
            :class="{ 'search-expanded': searchExpanded }"
          >
            <button class="search-toggle-btn" @click="toggleSearch">
              {{ searchExpanded ? '✕' : '🔍' }}
            </button>
            <div v-if="searchExpanded" class="search-input-wrapper">
              <input
                ref="searchInput"
                v-model="searchQuery"
                type="text"
                class="search-input"
                placeholder="ค้นหาเกมส์..."
                @input="onSearchInput"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="filteredGames.length > 0" class="games-grid">
      <div v-for="game in displayedGames" :key="game.id" class="game-grid-item">
        <GameCard
          :game="game"
          :show-online-count="showOnlineCount"
          :show-percentage="showPercentage"
          @game-select="handleGameSelect"
          @show-login-modal="$emit('show-login-modal')"
        />
      </div>
      <!-- Load more sentinel for virtual scrolling -->
      <div
        v-if="hasMoreGames"
        ref="loadMoreSentinel"
        class="load-more-sentinel"
      ></div>
    </div>

    <!-- Fallback for empty state -->
    <div v-else-if="!loading && games.length === 0" class="empty-state">
      <div class="empty-message">
        ไม่พบเกมส์
      </div>
    </div>

    <!-- No search results -->
    <div
      v-else-if="!loading && games.length > 0 && filteredGames.length === 0"
      class="empty-state"
    >
      <div class="empty-message">
        ไม่พบเกมส์ที่ค้นหา
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="loading-message">
        กำลังโหลดเกมส์...
      </div>
    </div>
  </div>
</template>

<script>
import GameCard from './GameCard.vue'
import BreadcrumbNav from './BreadcrumbNav.vue'

export default {
  name: 'GameGridContent',
  components: {
    GameCard,
    BreadcrumbNav
  },
  props: {
    games: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    showOnlineCount: {
      type: Boolean,
      default: true
    },
    breadcrumbItems: {
      type: Array,
      default: null
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    backButtonText: {
      type: String,
      default: '← กลับ'
    },
    showPercentage: {
      type: Boolean,
      default: false
    },
    menuTitle: {
      type: String,
      default: 'เกมส์'
    }
  },
  data() {
    return {
      // Internal search state
      searchExpanded: false,
      searchQuery: '',
      // Debounced search for performance
      debouncedSearchQuery: '',
      searchDebounceTimer: null,
      // Virtual scrolling state
      batchSize: 24,
      loadedBatches: 1,
      loadMoreObserver: null
    }
  },
  computed: {
    // Filtered games using debounced query for performance
    filteredGames() {
      if (!this.debouncedSearchQuery.trim()) {
        return this.games
      }
      const query = this.debouncedSearchQuery.toLowerCase()
      return this.games.filter(
        game => game.gameName && game.gameName.toLowerCase().includes(query)
      )
    },

    // Virtual scrolling: only render visible batch of games
    displayedGames() {
      return this.filteredGames.slice(0, this.loadedBatches * this.batchSize)
    },

    // Check if there are more games to load
    hasMoreGames() {
      return this.displayedGames.length < this.filteredGames.length
    },

    computedBreadcrumbItems() {
      // Use prop if provided, otherwise use default breadcrumb
      if (this.breadcrumbItems) {
        return this.breadcrumbItems
      }
      return [
        {
          label: 'หน้าหลัก',
          clickable: true,
          action: 'home'
        },
        {
          label: `${this.menuTitle} [${this.games ? this.games.length : 0}]`,
          clickable: false
        }
      ]
    }
  },
  methods: {
    handleGameSelect(game) {
      // Simple pass-through - GameCard now handles auth
      this.$emit('game-select', game)
    },

    // Search methods
    toggleSearch() {
      if (this.searchExpanded) {
        this.closeSearch()
      } else {
        this.searchExpanded = true
        this.$nextTick(() => {
          const { searchInput } = this.$refs
          if (searchInput) {
            searchInput.focus()
          }
        })
      }
    },

    closeSearch() {
      this.searchExpanded = false
      this.searchQuery = ''
    },

    // Debounced search input handler (300ms delay for performance)
    onSearchInput(event) {
      this.searchQuery = event.target.value
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearchQuery = this.searchQuery
        // Reset pagination when search changes
        this.loadedBatches = 1
      }, 300)
    },

    // Breadcrumb navigation
    handleBreadcrumbClick(action) {
      if (action === 'home') {
        this.$emit('navigate-home')
      }
    },

    // Virtual scrolling: load more games when sentinel is visible
    loadMoreGames() {
      if (this.hasMoreGames) {
        this.loadedBatches += 1
      }
    },

    // Setup intersection observer for infinite scroll
    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '200px',
        threshold: 0
      }

      this.loadMoreObserver = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && this.hasMoreGames) {
          this.loadMoreGames()
        }
      }, options)

      this.$nextTick(() => {
        const sentinel = this.$refs.loadMoreSentinel
        if (sentinel) {
          this.loadMoreObserver.observe(sentinel)
        }
      })
    },

    // Cleanup intersection observer
    cleanupIntersectionObserver() {
      if (this.loadMoreObserver) {
        this.loadMoreObserver.disconnect()
        this.loadMoreObserver = null
      }
    }
  },
  watch: {
    // Re-setup observer when games change
    games: {
      handler() {
        this.loadedBatches = 1
        this.$nextTick(() => {
          this.cleanupIntersectionObserver()
          this.setupIntersectionObserver()
        })
      },
      immediate: false
    },
    // Re-setup observer when filtered results change significantly
    hasMoreGames(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const sentinel = this.$refs.loadMoreSentinel
          if (sentinel && this.loadMoreObserver) {
            this.loadMoreObserver.observe(sentinel)
          }
        })
      }
    }
  },
  mounted() {
    this.setupIntersectionObserver()
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounceTimer)
    this.cleanupIntersectionObserver()
  },
  emits: ['game-select', 'show-login-modal', 'breadcrumb-click', 'back-click']
}
</script>

<style lang="scss" scoped>
.game-grid-content {
  width: 100%;
}

// Content header styles
.content-header {
  padding: 0 10px 15px;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    padding: 0 15px 15px;
  }
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 15px;
  }
}

.back-button-row {
  margin-bottom: 10px;
}

.back-btn {
  background: rgba(212, 175, 55, 0.2);
  border: 1px solid rgba(212, 175, 55, 0.3);
  border-radius: 8px;
  padding: 8px 12px;
  color: #f5deb3;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(212, 175, 55, 0.3);
    transform: translateX(-2px);
  }

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 6px 10px;
  }
}

.games-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 15px;
  padding: 0 20px 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
    padding: 0 15px 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 0 12px 12px;
  }
}

.game-grid-item {
  cursor: pointer;
  transition: transform 0.2s ease;
  transform: translate3d(0, 0, 0);

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-2px);
    }
  }

  @media (max-width: 480px) {
    &:hover {
      transform: translate3d(0, 0, 0);
    }

    &:active {
      transform: translate3d(0, -1px, 0) scale(0.98);
      transition: transform 0.1s ease;
    }
  }
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: rgba(255, 255, 255, 0.7);
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
}

.empty-message {
  font-size: 1.2rem;
  text-align: center;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: rgba(255, 255, 255, 0.7);
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
}

.loading-message {
  font-size: 1.1rem;
  text-align: center;
}

// Load more sentinel for virtual scrolling
.load-more-sentinel {
  width: 100%;
  height: 1px;
  grid-column: 1 / -1;
}

// Search styles
.search-section {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
}

.search-container {
  position: relative;
  height: 40px;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;

  &:not(.search-expanded) {
    width: 40px;
  }

  &.search-expanded {
    width: 250px;

    @media (max-width: 768px) {
      width: 200px;
    }

    @media (max-width: 480px) {
      width: 200px;
    }
  }
}

.search-toggle-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 20px;
  background: rgba(209, 55, 212, 0.2);
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  &:hover {
    background: rgba(209, 55, 212, 0.3);
    transform: scale(1.05);
  }
}

.search-input-wrapper {
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  height: 40px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  overflow: hidden;
  animation: expandSearch 0.3s ease;
  z-index: 1;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 0 15px 0 45px;
  font-size: 14px;
  background: transparent;
  color: #333;

  &::placeholder {
    color: #999;
  }
}

@media (max-width: 768px) {
  .search-section {
    justify-content: flex-end;
  }
}

@keyframes expandSearch {
  from {
    opacity: 0;
    transform: scaleX(0);
  }
  to {
    opacity: 1;
    transform: scaleX(1);
  }
}
</style>
